'use client';

import { CalendarDays, Globe, TrendingUp } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import type {
  SalaryInsightsCountryCode,
  SalaryInsightsExperience,
  SalaryInsightsProfession,
} from '../types';

import styles from '../salary-insights.module.css';

interface SalaryEstimatorProps {
  profession: SalaryInsightsProfession;
  onSalaryChange?: (salary: string) => void;
}

const COUNTRY_OPTIONS: { value: SalaryInsightsCountryCode; label: string; flag: string }[] = [
  { value: 'US', label: 'United States', flag: '🇺🇸' },
  { value: 'IN', label: 'India', flag: '🇮🇳' },
  { value: 'UK', label: 'United Kingdom', flag: '🇬🇧' },
  { value: 'DE', label: 'Germany', flag: '🇩🇪' },
  { value: 'AE', label: 'United Arab Emirates', flag: '🇦🇪' },
];

export function SalaryEstimator({ profession, onSalaryChange }: SalaryEstimatorProps) {
  const [country, setCountry] = useState<SalaryInsightsCountryCode>(
    profession.estimator.defaultCountry,
  );
  const [experience, setExperience] = useState<SalaryInsightsExperience>(
    profession.estimator.defaultExperience,
  );
  const salary = useMemo(
    () => profession.salaryData[country].ranges[experience],
    [country, experience, profession],
  );
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    // Trigger a quick pulse animation whenever the derived salary range changes.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setPulse(true);
    const timer = setTimeout(() => setPulse(false), 180);
    if (onSalaryChange) {
      onSalaryChange(salary);
    }
    return () => clearTimeout(timer);
  }, [salary, onSalaryChange]);

  return (
    <div className="relative z-10 mx-auto w-full max-w-3xl -mt-20 rounded-3xl border border-slate-100/80 bg-white p-8 shadow-2xl shadow-slate-900/10 md:-mt-24 md:p-10">
      <div className="mb-8 text-center">
        <h2 className="mb-2 text-2xl font-bold tracking-tight text-slate-900 md:text-3xl">
          Interactive Salary Estimator
        </h2>
        <p className="text-base text-slate-500 md:text-lg">
          Select your location and experience to see average market rates.
        </p>
      </div>

      <div className="mb-8 grid gap-5 md:grid-cols-2">
        <div className="text-left">
          <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Globe className="h-4 w-4 text-[#3bbdad]" />
            Location
          </label>
          <Select
            value={country}
            onValueChange={(value) => setCountry(value as SalaryInsightsCountryCode)}
          >
            <SelectTrigger className="h-14 cursor-pointer rounded-xl border-2 border-slate-200 bg-white px-4 text-base font-semibold text-slate-700 shadow-sm transition-colors hover:border-[#3bbdad]/40 focus:border-[#3bbdad] focus:ring-[#3bbdad]">
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  <span className="mr-2">{option.flag}</span>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="text-left">
          <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-700">
            <CalendarDays className="h-4 w-4 text-[#3bbdad]" />
            Experience
          </label>
          <Select
            value={experience}
            onValueChange={(value) => setExperience(value as SalaryInsightsExperience)}
          >
            <SelectTrigger className="h-14 cursor-pointer rounded-xl border-2 border-slate-200 bg-white px-4 text-base font-semibold text-slate-700 shadow-sm transition-colors hover:border-[#3bbdad]/40 focus:border-[#3bbdad] focus:ring-[#3bbdad]">
              <SelectValue placeholder="Select experience" />
            </SelectTrigger>
            <SelectContent>
              {(['entry', 'mid', 'senior', 'lead'] as SalaryInsightsExperience[]).map((level) => (
                <SelectItem key={level} value={level}>
                  {profession.estimator.experienceLabels[level]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-50 p-6 md:p-8">
        <div className="mb-1 flex items-center justify-center gap-2 text-sm font-medium uppercase tracking-wide text-slate-500">
          <TrendingUp className="h-4 w-4" />
          Estimated Annual Range
        </div>
        <div
          className={`bg-gradient-to-r from-[#3bbdad] to-[#0d9488] bg-clip-text text-5xl font-black text-transparent md:text-6xl ${styles.salaryResult} ${pulse ? styles.salaryResultPulse : ''}`}
          data-testid="salary-result"
        >
          {salary}
        </div>
        <p className="mt-3 text-sm text-slate-400">{profession.estimator.note}</p>
      </div>

      <div className="mt-8">
        <Button
          asChild
          className="h-auto rounded-full bg-slate-900 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-slate-900/20 transition-transform hover:-translate-y-0.5 hover:bg-slate-800"
        >
          <a href="/salary-insights-report">
            Get Your Personalized Report
          </a>
        </Button>
      </div>
    </div>
  );
}
